/* =======================================================================
   Knowledge Base — interactive walkthrough. Azure item 416, phase 3.

   "Take me there" in an article hands the reader to the real screen and
   talks them through it: the page dims, a rounded hole is left around the
   one control that matters, and a tooltip points at it. When the reader
   does the thing, it moves on by itself. At the end the contrast comes back
   and a small badge stays in the header as the way back in.

   Ported from the mockup on 10 Sep 2026. The engine is deliberately kept
   imperative and DOM-driven (refs, not React state, for the overlay
   geometry): it is the tested code, and geometry that re-renders through
   React on every scroll would be both slower and harder to reason about.

   Two decisions worth keeping if this grows:

   * Steps advance off STATE, not off clicks. Each step owns a done()
     predicate read every 100ms, so the tour cannot fall out of step with
     the app and needs no hooks inside the app's click handlers. It also
     makes Resume land on the first thing still to do.

   * Whenever a walkthrough brings the reader to a new screen, the next step
     is a view step ({ kind:'info', view:true }, no target): one undimmed tile
     saying what the screen shows, moved on only by Next. Readers who click
     straight through otherwise never learn where they have landed.

   * Targets are data-tour="…" attributes on the real Work Orders screen
     (BranchBar, CreateButton, WO.js, createwomodal.js, WoTable). Restyle
     freely; the tour keeps working. The app exposes two things the tour
     cannot read from the URL or DOM through kbSession.js: how many work
     orders have been created this session, and whether a tour is running.
   ======================================================================= */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KB_BASE, KB_WALK_START, SESSION, announce, woCreatedCount, woLastCreatedId, techsSavedCount, woEditedCount, filesUploadedCount, setWalkthroughRunning, setLastArticle, readJson, writeJson } from './kbSession';
import './kb.css';

/* ---------------- what the real app tells us ---------------- */
const qs = () => new URLSearchParams(window.location.search);
const onWorkList = () => window.location.pathname === '/work';
const branchPicked = () => onWorkList() && Number(qs().get('branch') || 0) > 0;
const dialogOpen = () => onWorkList() && qs().get('create') === '1';
const searching = () => onWorkList() && !!(qs().get('keyword') || '').trim();
const filtered = () => onWorkList() && !!qs().get('criteria');
const filterOpen = () => onWorkList() && !!document.querySelector('[data-tour="wo-filter-panel"]');
const filled = key => !!document.querySelector('[data-tour="' + key + '"][data-tour-filled]');
/* a step's target is a selector, or a function returning one — the finish step
   points at the row that was just created, whose id is not known in advance */
const sel = s => (typeof s.target === 'function' ? s.target() : s.target);
/* can the table show WO# and Date Started at the same time? */
const sortBothFit = () => {
  const t = document.querySelector('[data-tour="wo-table"]');
  const a = document.querySelector('[data-tour="wo-sort-number"]'), b = document.querySelector('[data-tour="wo-sort-date"]');
  return !!(t && a && b) && b.getBoundingClientRect().right - a.getBoundingClientRect().left <= t.clientWidth;
};

/* ---------------- the walkthroughs ---------------- */
/* Has the reader created a work order since this walkthrough was last set
   up? Submitting clears the form, so the form cannot answer it; the session
   counter only ever goes up, so a mark taken at reset() survives everything
   except deliberately starting again. */
let wtMark = 0;
const wtMadeOne = () => woCreatedCount() > wtMark;
/* Marks for the find walkthrough, set by its own done() predicates: once the
   reader has searched, that stays true even after they clear the search —
   which is the next step. Cleared by its reset(). */
const seen = { search: false };

/* For the assign walkthrough. The work order page and the Assign window hold
   their state in React, so the tour reads the DOM: data-tour anchors in
   ServiceCall.jsx and the AssignTech components, with each row carrying its
   chosen user and role as data-tour-user / data-tour-role (-1 = none). */
const onDetail = () => /^\/work\/\d+\/?$/.test(window.location.pathname);
const assignOpen = () => !!document.querySelector('[data-tour="wo-assign-dialog"]');
const assignRows = () => [...document.querySelectorAll('[data-tour="assign-row"]')]
  .map(r => ({ u: String(r.dataset.tourUser), r: String(r.dataset.tourRole) }));
let techMark = 0;                                   /* saves counted before this run */
const savedTechs = () => techsSavedCount() > techMark;
let teamAtOpen = null;                              /* users in the rows when Team was reached */
const teamPicked = () => !!teamAtOpen && assignRows().some(r => r.u !== '-1' && !teamAtOpen.includes(r.u));

/* For the edit walkthrough. The pencil opens the create dialog in edit mode
   (createwomodal.js with an order), on the work order page itself. */
const editOpen = () => onDetail() && !!document.querySelector('[data-tour="wo-dialog"]');
let editMark = 0;                                   /* edits saved before this run */
const savedEdit = () => woEditedCount() > editMark;

/* For the upload walkthrough. The four sections share one upload window
   (uploadModalUI's ModalShell), told apart by its title; which one the
   reader opened decides the wording later and where the finish points. */
const UPLOAD_KINDS = [
  ['photos', /photo/i, 'Photos/Videos'], ['docs', /document/i, 'Technical Documents'],
  ['proposals', /proposal/i, 'Proposals'], ['invoices', /invoice/i, 'Invoices']];
let uploadKind = null;
const uploadDialog = () => {
  if (!onDetail()) return null;
  const d = [...document.querySelectorAll('[data-tour="modal-shell"]')]
    .find(el => /^\s*upload/i.test((el.querySelector('h2') || {}).textContent || ''));
  if (d){ const t = d.querySelector('h2').textContent; const k = UPLOAD_KINDS.find(([, re]) => re.test(t)); if (k) uploadKind = k[0]; }
  return d || null;
};
const uploadOpen = () => !!uploadDialog();
const uploadStaged = () => uploadOpen() && !document.querySelector('[data-tour="upload-dropzone"]');
const uploadLabel = () => { const k = UPLOAD_KINDS.find(([key]) => key === uploadKind); return k ? k[2] : 'the section'; };
let uploadMark = 0;                                 /* uploads before this run */
const uploaded = () => filesUploadedCount() > uploadMark;

/* Scroll a container to a new position so the reader sees where they are
   being taken: ease-in-out, duration by distance, with the overlay's rings
   and tiles hidden throughout (.wt.arriving) and popped in once the page has
   settled. Frames come from rAF where it ticks and a timer where it does not
   (see the watcher); a reader who scrolls mid-way takes over and the tiles
   appear at once. Reduced motion jumps, then pops in the same way. */
const scrollWithArrival = (sc, delta) => {
  const wt = document.querySelector('.kb-wt .wt');
  if (!sc || Math.abs(delta) < 4){ return; }
  const from = sc.scrollTop, to = Math.max(0, Math.min(from + delta, sc.scrollHeight - sc.clientHeight));
  const land = () => setTimeout(() => wt && wt.classList.remove('arriving'), 180);  /* after .scrolling clears */
  if (wt) wt.classList.add('arriving');
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce){ sc.scrollTop = to; land(); return; }
  const dist = Math.abs(to - from), dur = Math.min(1100, Math.max(500, 380 + dist * 0.45));
  const ease = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);   /* easeInOutCubic */
  const t0 = performance.now();
  let expected = from;
  const tick = () => {
    if (Math.abs(sc.scrollTop - expected) > 3){ land(); return; }          /* the reader took over */
    const t = Math.min(1, (performance.now() - t0) / dur);
    expected = Math.round(from + (to - from) * ease(t));
    sc.scrollTop = expected;
    if (t < 1) frame(); else land();
  };
  const frame = () => {
    let fired = false;
    const once = () => { if (!fired){ fired = true; tick(); } };
    if (window.requestAnimationFrame) window.requestAnimationFrame(once);
    setTimeout(once, 20);
  };
  frame();
};

/* Shared opening for any walkthrough that works on one work order: find it
   (an open step) and then say what the work order page is (a view step), so
   the reader never has to leave the tour to go looking for the job. */

/* An open step. Finding a job can take any route — search, a branch, the
   filter, or just spotting it — so nothing is dimmed but the app's side menu,
   every tool is outlined, and the one thing that moves the tour on is opening
   a work order. */
const findJobStep = () => ({
  dimOnly: '[data-tour="app-sidemenu"]', dock: true, noSkip: true,
  /* sorting left out on purpose: rarely how anyone finds a job (Gavin) */
  outline: ['[data-tour="wo-search"]', '[data-tour="wo-branches"]', '[data-tour="wo-filter"]'],
  also: '[data-tour="wo-search"] ul, [data-tour="wo-filter-panel"] ul',
  yieldTo: '[data-tour="date-picker"]',
  title: 'Find your work order',
  body: () => '<p>Use whichever is quickest — everything outlined works:</p>' +
        '<ul>' +
        '<li><b>Search</b> for the work order number, the customer, or a word from the description.</li>' +
        '<li><b>Pick its branch</b>' + (document.querySelector('[data-tour="wo-filter"]')
          ? ', then <span class="k">Filter</span> by status, customer, technician and more.'
          : ' — that also brings up <span class="k">Filter</span>.') + '</li>' +
        '<li>Or just <b>click it</b> in the list if you can already see it.</li>' +
        '</ul>',
  doIt: 'Click the work order when you find it',
  done: () => onDetail() });

/* New screen, so one tile says what is on it — for readers who click straight
   through. Also where a start from an open work order lands. `next` finishes
   "when you're ready to …"; `done` is the walkthrough's own "behind you". */
const viewJobStep = (next, done) => ({
  kind: 'info', view: true,
  title: 'This is the work order',
  body: '<p>Everything about this one job lives on this page. From the top: the customer’s details, ' +
        'the service panel — type, status, NTE, description, and on the right who is <b>Assigned To</b> — ' +
        'then photos, proposals, notes, documents, invoices, purchase orders, the timesheet and its history.</p>' +
        '<p>Press <b>Next</b> when you’re ready to ' + next + '.</p>',
  done });

const WALKTHROUGHS = {
  'create-work-order': {
    label: 'Create a work order',
    /* The screen the article's first step assumes: the list, on All, nothing
       open. Work orders already created are left alone. */
    reset(navigate){
      wtMark = woCreatedCount();
      navigate('/work?branch=0', { replace: true });
    },
    /* Work in progress worth not throwing away: the dialog open, or a
       required field already chosen. A branch on its own does not count —
       reselecting one is the walkthrough's own first step — and neither does
       a finished job, or the demo could never be run from the top twice. */
    midTask(){ return dialogOpen() || filled('wo-customer') || filled('wo-service'); },
    steps: [
      { kind: 'intro',
        title: 'Let’s create a work order',
        body: '<p>You’re on the <b>Work Orders</b> tab. There are four things to do: pick a branch, ' +
              'open the form, fill in the two required fields, and submit.</p>' +
              '<p>I’ll point at each one. Do it for real — this is the screen, not a picture of it.</p>' },

      { target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Pick the branch first',
        body: '<p>A work order belongs to one branch, and its number comes from that branch’s prefix. ' +
              'You cannot change it afterwards.</p>' +
              '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">+ Create</span> button at all. ' +
              'That is why this step comes first.</p>',
        doIt: 'Click the branch the job is on',
        done: () => branchPicked() || wtMadeOne() },

      { target: '[data-tour="wo-create"]', place: 'bottom',
        title: 'Now + Create is there',
        body: '<p>It appeared the moment a single branch was selected — along with ' +
              '<span class="k">Filter</span>.</p>',
        doIt: 'Click <b>+ Create</b>',
        done: () => dialogOpen() || wtMadeOne() },

      { target: '[data-tour="wo-customer"]', also: '[data-tour="wo-customer"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the customer',
        body: '<p>Required. The customer has to exist in Makani already — if it does not, you have to ' +
              'add it from the Customers tab first and come back.</p>' +
              '<p>There are a lot of them, so type to narrow the list.</p>',
        doIt: 'Open the list and pick a customer',
        done: () => filled('wo-customer') || wtMadeOne() },

      { target: '[data-tour="wo-service"]', also: '[data-tour="wo-service"] ul', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Choose the type of service',
        body: '<p>Also required. Service Call, Quoted Job, PM, Parts Only, Compressor Rebuild or Admin Hours.</p>' +
              '<p>If the one you need is missing, <span class="k">+ Add Service Type</span> beside the label adds it.</p>',
        doIt: 'Pick a type of service',
        done: () => filled('wo-service') || wtMadeOne() },

      { target: '[data-tour="wo-optional-start"]', targetEnd: '[data-tour="wo-description"]', also: '[data-tour="wo-dialog"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'The rest is optional',
        body: '<p>Everything between <b>NTE</b> and <b>Description</b> can be left exactly as it is. ' +
              'Fill in whatever you already know:</p>' +
              '<ul>' +
              '<li><b>NTE</b> — the not-to-exceed dollar amount. Defaults to $0.</li>' +
              '<li><b>Start Date</b> — defaults to today.</li>' +
              '<li><b>Asset</b> — the unit being worked on.</li>' +
              '<li><b>Customer Purchase Order #</b> — the customer’s PO number, if they gave you one.</li>' +
              '<li><b>Assign Primary Technician</b> — who is taking the job. Safe to leave blank now and ' +
                  'set later, though the job does not reach anyone until someone is assigned.</li>' +
              '<li><b>Description</b> — what is wrong.</li>' +
              '</ul>',
        done: () => wtMadeOne() },

      { target: '[data-tour="wo-description"]', place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'One of them is worth your time',
        body: '<p>The description is what the technician reads first, so be specific.</p>' +
              '<p>“Walk-in freezer running at 30 degrees” beats “freezer issue”.</p>',
        done: () => wtMadeOne() },

      { target: '[data-tour="wo-submit"]', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Submit',
        body: '<p>Notice it was greyed out until both required fields were set. That is the only thing ' +
              'stopping you — nothing else on the form is checked.</p>',
        doIt: 'Click <b>SUBMIT</b>',
        done: () => wtMadeOne() },

      { kind: 'finish', place: 'bottom',
        /* The row that was just created, by id, wherever the list puts it — the
           create succeeds before the list refetches, so for a moment the row is
           not there at all; until it is, the card sits centred with no ring, and
           the watcher lights the row the instant it appears. */
        target: () => (woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]'),
        /* The table scrolls sideways and Status is the column that falls off —
           the one thing this card is about. Bring it into view first. */
        prep(){
          const row = document.querySelector(woLastCreatedId() ? '#wo-row-' + woLastCreatedId() : '[data-tour="wo-row"]');
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center' });
          let p = row && row.parentElement;
          while (p && p !== document.body){ if (p.scrollWidth > p.clientWidth + 2){ p.scrollLeft = p.scrollWidth; break; } p = p.parentElement; }
        },
        title: 'That’s a work order',
        body: '<p>It is at the top of the list in <b>Open</b> status — not Requested — with its number ' +
              'assigned from the branch prefix.</p>' +
              '<p>It has no History yet: creating a work order is not itself logged. That is normal.</p>' }
    ]
  },

  'find-work-orders': {
    label: 'Find a work order',
    /* Nothing here is written to the database, so reset is just the clean
       list: All, no search, no filter. The marks make each step stick once
       done — clearing the search is the walkthrough's own step, so "you
       searched" must survive the search being cleared. */
    reset(navigate){
      seen.search = false;
      navigate('/work?branch=0', { replace: true });
    },
    midTask(){ return searching() || filterOpen() || filtered(); },
    /* Opening a work order is what finding one is for. When the reader does,
       the tour folds into the header badge instead of standing in their way. */
    pauseWhen: () => onDetail(),
    steps: [
      { kind: 'intro',
        title: 'Let’s find a work order',
        body: '<p>You’re on the <b>Work Orders</b> tab. Three tools do the work: <b>Search</b> at the top, ' +
              'the column headings for sorting, and <b>Filter</b> for anything narrower.</p>' +
              '<p>I’ll point at each one. Do it for real — this is the live list.</p>' },

      /* Once a search is in place the results are lit and outlined too, and
         clickable — a reader who only wanted the one job can open it here. */
      { target: '[data-tour="wo-search"]', place: 'bottom', placeOnMain: true,
        extra: () => (searching() ? '[data-tour="wo-table"]' : null),
        title: 'Search, from anywhere',
        body: () => '<p>This box searches the work order number, the customer and the description at once, ' +
              'so a job number, a customer name or a word like <i>freezer</i> all work.</p>' +
              '<p>The list narrows as you type and the match is highlighted in yellow.</p>' +
              (searching() ? '<p><b>Found it?</b> Click it to open it — the walkthrough will wait in the header.</p>' : ''),
        doIt: 'Type something — a customer name is a good start',
        /* manual: the list narrows on a debounce, so auto-advancing fired
           mid-word and took the tile away while the reader was still typing.
           done() still counts for resume. */
        manual: true,
        /* "behind you", not "true now": a reader already at the filter has
           moved past searching, whether or not they typed anything */
        done: () => { if (searching()) seen.search = true; return seen.search || filterOpen() || filtered(); } },

      { target: '[data-tour="wo-search-clear"]', place: 'bottom',
        title: 'Clear it the same way',
        body: '<p>The ⊗ inside the box clears the search and brings the whole list back. ' +
              'A search that is still in place is the usual reason a work order “is missing”.</p>',
        doIt: 'Click the ⊗',
        /* "no search in place" — so a reader who skipped the search step is
           not stuck here, and one who cleared it moves on the moment they do */
        done: () => !searching() },

      /* Only these two headings have a click handler (WoTable.js); the rest are
         plain labels. Directions checked on devgav 21 Sep 2026. */
      { target: '[data-tour="wo-sort-number"]', extra: '[data-tour="wo-sort-date"]', place: 'bottom', kind: 'info',
        /* The two headings sit either end of the table, which scrolls sideways
           at laptop widths. Bring both into view when they fit; when they do
           not, leave WO# in view and say where the other one is. */
        prep(){
          const t = document.querySelector('[data-tour="wo-table"]');
          if (!t || !sortBothFit()) return;
          const a = document.querySelector('[data-tour="wo-sort-number"]'), b = document.querySelector('[data-tour="wo-sort-date"]');
          t.scrollLeft += b.getBoundingClientRect().right - t.getBoundingClientRect().right + 8;
          if (a.getBoundingClientRect().left < t.getBoundingClientRect().left) t.scrollLeft = 0;
        },
        title: 'Sort by WO# or Date Started',
        body: () => '<p>These are the only two headings that sort. The first click puts the newest work orders at the top, ' +
              'a second click reverses it, and an arrow beside the heading shows which one is sorting.</p>' +
              '<p>Sorting by one clears the other. The rest of the headings are labels only.</p>' +
              (sortBothFit() ? '' : '<p><b>Date Started</b> is further right — scroll the table sideways to reach it.</p>'),
        /* an info tile still needs a way to be "past": a reader who has raced
           ahead to the filter should not be pulled back to read about sorting */
        done: () => filterOpen() || filtered() },

      { target: '[data-tour="wo-branches"]', place: 'bottom',
        title: 'Filter needs a branch',
        body: '<p>Look at the top right: on <span class="k">All</span> there is no <span class="k">Filter</span> button. ' +
              'Filtering only works inside a single branch.</p>',
        doIt: 'Click a branch tab',
        done: () => branchPicked() || filterOpen() || filtered() },

      { target: '[data-tour="wo-filter"]', place: 'bottom',
        title: 'Now Filter is there',
        body: '<p>It appeared with the branch — and so did <span class="k">Download</span>, which exports ' +
              'whatever the filter finds as an Excel file.</p>',
        doIt: 'Click <b>Filter</b>',
        done: () => filterOpen() || filtered() },

      /* the five dropdowns open a <ul> inside the panel that hangs below it;
         the two date fields open a full-screen date picker instead */
      { target: '[data-tour="wo-filter-panel"]', also: '[data-tour="wo-filter-panel"] ul', yieldTo: '[data-tour="date-picker"]',
        place: 'bottom', kind: 'info',
        title: 'Seven ways to narrow it',
        body: '<p><b>Service Type</b>, <b>Customer</b>, <b>Status</b>, <b>Technician</b>, <b>Start date</b>, ' +
              '<b>End date</b> and <b>Quoted by</b>. Any combination.</p>' +
              '<p>Set one now — <b>by Status</b> is the quickest to try.</p>',
        done: () => filtered() },

      { target: '[data-tour="wo-filter-apply"]', place: 'left',
        title: 'Apply',
        body: '<p>Nothing changes until you click it. The filter is written into the page address, so the ' +
              'browser’s Back button undoes it and a reload keeps it.</p>',
        doIt: 'Click <b>Apply</b>',
        done: () => filtered() },

      { kind: 'finish', place: 'left', target: '[data-tour="wo-filter-clear"]',
        title: 'That’s the filtered list',
        body: '<p>Two things to remember. <b>Clear</b>, right here, resets every field at once — the filter ' +
              'otherwise stays on while you move between branches.</p>' +
              '<p>And the list loads in batches: if a job seems to be missing, click <b>LOAD MORE</b> at the ' +
              'bottom before deciding it does not exist.</p>' }
    ]
  },

  'assign-technicians': {
    label: 'Assign a technician',
    /* The first three steps find the job, so the reader never has to leave
       the walkthrough to go looking for it. Started from a work order that is
       already open (the drawer), midTask() is true and the landing skips
       straight to that job's Assigned To. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ techMark = techsSavedCount(); teamAtOpen = null; },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail() || assignOpen(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s put someone on a job',
        body: '<p>Three things: find the work order, open it, and add the person in the <b>Assign</b> window.</p>' +
              '<p>I’ll go with you the whole way, starting with finding the job.</p>' },

      findJobStep(),
      viewJobStep('assign a technician', () => assignOpen() || savedTechs()),

      { target: '[data-tour="wo-assigned"]', place: 'left',
        title: 'Assigned To',
        /* the button is not rendered on a Requested job, or without
           wo:assignTech — say so rather than point at nothing */
        body: () => document.querySelector('[data-tour="wo-assign-open"]')
          ? '<p>Everyone on the job is listed here. The button underneath reads <span class="k">Edit</span> ' +
            'once someone is assigned, and <span class="k">+ Add</span> while nobody is.</p>' +
            '<p>Not the pencil icon — that opens a different form, which does not handle technicians.</p>'
          : '<p>There is no button here, so this job cannot be assigned yet. Either it is still ' +
            '<b>Requested</b> — approve it first — or your role does not include assigning technicians.</p>',
        doIt: 'Click <b>Edit</b> or <b>+ Add</b>',
        done: () => assignOpen() || savedTechs() },

      { target: '[data-tour="assign-rows"]', also: '[data-tour="assign-menu"]',
        place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        /* what the rows held when the window opened, so "picked someone"
           means someone new — not a name that was already there */
        prep(){ teamAtOpen = assignRows().map(r => r.u); },
        title: 'Pick the person',
        body: '<p>Each row is one person. Open <b>Team</b> and choose them — type to narrow the list.</p>' +
              '<p>Everyone already on the job has a row. To add someone alongside them, click ' +
              '<span class="k">+ Add more</span> below for an empty row first.</p>',
        doIt: 'Choose someone under <b>Team</b>',
        done: () => teamPicked() || savedTechs() },

      { target: '[data-tour="assign-rows"]', also: '[data-tour="assign-menu"]',
        place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        title: 'Give them a role',
        body: '<p><b>Primary technician</b> is the lead — the name shown in the list, and on purchase ' +
              'order emails. <b>Technician</b> is anyone else on the job.</p>' +
              '<p>Keep it to one Primary unless you mean it. Nothing stops you choosing two.</p>',
        doIt: 'Pick a <b>Role</b>',
        done: () => (teamPicked() && assignRows().every(r => r.u === '-1' || r.r !== '-1')) || savedTechs() },

      { target: '[data-tour="assign-add-more"]', place: 'right', avoid: '[data-tour="wo-assign-dialog"]', kind: 'info',
        title: 'More than one person?',
        body: '<p><span class="k">+ Add more</span> adds another row. The trash icon at the end of a row takes ' +
              'that person off the job when you save.</p>',
        done: () => savedTechs() },

      { target: '[data-tour="assign-save"]', place: 'right', avoid: '[data-tour="wo-assign-dialog"]',
        title: 'Save',
        body: '<p>It stays greyed out while any row has a person but no role — that is the only check.</p>',
        doIt: 'Click <b>SAVE</b>',
        done: () => savedTechs() },

      { kind: 'finish', target: '[data-tour="wo-assigned"]', place: 'left',
        title: 'They’re on the job',
        body: '<p>The names under <b>Assigned To</b> are who has it now. The icons beside each one are set ' +
              'from the technician app — the article explains what each means.</p>' +
              '<p>To change anyone later, come back to <span class="k">Edit</span> here.</p>' }
    ]
  },

  'edit-work-order': {
    label: 'Edit a work order',
    /* Same opening as assign: find the job, say what the page is. The window
       is the create dialog in edit mode, so its field anchors are shared with
       the create walkthrough; wo-status and wo-number exist only here. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ editMark = woEditedCount(); },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s edit a work order',
        body: '<p>Three things: find the work order, open it, and make your changes in the ' +
              '<b>Edit Work Order</b> window.</p>' +
              '<p>I’ll go with you the whole way, starting with finding the job.</p>' },

      findJobStep(),
      viewJobStep('make your changes', () => editOpen() || savedEdit()),

      { target: '[data-tour="wo-edit-open"]', place: 'bottom',
        title: 'The pencil',
        /* rendered only with the wo:update permission */
        body: () => document.querySelector('[data-tour="wo-edit-open"]')
          ? '<p>The pencil at the top right of the service panel, beside ' +
            '<span class="k">SERVICE TICKET</span>, opens the <b>Edit Work Order</b> window.</p>'
          : '<p>There is no pencil here, so your role does not include editing work orders.</p>',
        doIt: 'Click the pencil',
        done: () => editOpen() || savedEdit() },

      /* the status list is the app's own <ul>, hanging below the dropdown */
      { target: '[data-tour="wo-status"]', also: '[data-tour="wo-status"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'Status is up here',
        body: '<p>The <b>Status</b> dropdown sits in the top right corner of the window — easy to miss.</p>' +
              '<p>Any status can be picked at any time; nothing makes a job go through them in order.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-number"]', place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'What this window cannot change',
        body: '<p>The <b>work order number</b> is fixed for the life of the job, and so is its branch — ' +
              'the number is built from the branch prefix.</p>' +
              '<p>Technicians are not in here either. They are changed from <span class="k">Edit</span> under ' +
              '<b>Assigned To</b> on the work order page.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-customer"]', targetEnd: '[data-tour="wo-description"]', also: '[data-tour="wo-dialog"] ul',
        place: 'right', avoid: '[data-tour="wo-dialog"]', kind: 'info',
        title: 'Change what you need',
        body: '<p><b>Customer</b> and <b>Type of Service</b> are required, as they were when the job was created.</p>' +
              '<p>The rest can be changed or left as they are: <b>NTE</b>, <b>Start Date</b>, <b>Asset</b>, ' +
              '<b>Customer Purchase Order #</b> and <b>Description</b>.</p>',
        done: () => savedEdit() },

      { target: '[data-tour="wo-submit"]', place: 'right', avoid: '[data-tour="wo-dialog"]',
        title: 'Submit',
        body: '<p>Nothing is saved until you click it. <span class="k">CANCEL</span> closes the window ' +
              'without saving.</p>',
        doIt: 'Click <b>SUBMIT</b>',
        done: () => savedEdit() },

      { kind: 'finish', target: '[data-testid="wo-status-badge"]', place: 'bottom',
        title: 'Saved',
        body: '<p>The service panel now shows the new details. A change of status is also recorded in ' +
              '<b>History</b> at the bottom of the page, with who made it and when.</p>' +
              '<p>You can come back to the pencil as often as you need — it does not lock, even on a Billed job.</p>' }
    ]
  },

  'work-order-documents': {
    label: 'Add files to a work order',
    /* Same opening as assign and edit. The file picker itself is the
       operating system's and cannot be pointed at; the tour waits for the
       window to show the chosen files instead. */
    onHome: () => onWorkList() || onDetail(),
    begin(){ uploadMark = filesUploadedCount(); uploadKind = null; },
    reset(navigate){ navigate('/work?branch=0', { replace: true }); },
    midTask(){ return onDetail(); },
    steps: [
      { kind: 'intro',
        title: 'Let’s add a file to a work order',
        body: '<p>Three things: find the work order, pick the section the file belongs in, and upload it.</p>' +
              '<p>Photos, proposals, invoices and technical documents all work the same way.</p>' },

      findJobStep(),
      viewJobStep('add a file', () => uploadOpen() || uploaded()),

      /* An open step with a tile per section. The page is scrolled so all
         four Upload buttons are on screen (or as many as fit); the Photos
         tile is the main one, carrying the count and the buttons, and the
         other three are callouts. Clicking any Upload moves the tour on. */
      { dimOnly: '[data-tour="app-sidemenu"]', noSkip: true,
        target: '[data-tour="wo-upload-photos"]', place: 'left',
        outline: ['[data-tour="wo-upload-photos"]', '[data-tour="wo-upload-proposals"]',
                  '[data-tour="wo-upload-docs"]', '[data-tour="wo-upload-invoices"]', '[data-tour="wo-add-form"]'],
        /* Add Form opens its own window (AddFormModal), not an upload one;
           the tour steps aside while it is open rather than draw over it */
        yieldTo: '[data-tour="add-form-dialog"]',
        prep(){
          const btns = [...document.querySelectorAll('[data-tour^="wo-upload-"], [data-tour="wo-add-form"]')];
          if (!btns.length) return;
          /* the page's own scroller: the nearest ancestor that scrolls */
          let sc = btns[0].parentElement;
          while (sc && sc !== document.body && !(/auto|scroll/.test(getComputedStyle(sc).overflowY) && sc.scrollHeight > sc.clientHeight)) sc = sc.parentElement;
          if (!sc || sc === document.body) return;
          const r = sc.getBoundingClientRect();
          const tops = btns.map(b => b.getBoundingClientRect().top), bottoms = btns.map(b => b.getBoundingClientRect().bottom);
          const top = Math.min(...tops), bottom = Math.max(...bottoms);
          /* centre the span when it fits; otherwise start it near the top */
          const room = r.height, span = bottom - top;
          const want = span + 160 < room ? (top + bottom) / 2 - (r.top + room / 2) : top - r.top - 120;
          scrollWithArrival(sc, want);
        },
        title: 'Pick the section',
        /* each Upload button needs that section's add permission */
        body: () => (document.querySelector('[data-tour^="wo-upload-"]')
          ? '<p><b>Photos/Videos</b> — pictures and video from the job. Photos can be printed on the service ticket.</p>'
          : '<p>There are no <span class="k">Upload</span> buttons on this work order, so your role does not ' +
            'include adding files.</p>'),
        callouts: [
          { target: '[data-tour="wo-upload-proposals"]', title: 'Proposals',
            body: '<p>Proposals and quotes for this job.</p>' },
          { target: '[data-tour="wo-upload-invoices"]', title: 'Invoices',
            body: '<p>Invoices for this job.</p>' },
          { target: '[data-tour="wo-upload-docs"]', title: 'Technical Documents',
            body: '<p>Spec sheets, wiring diagrams and manuals for the equipment.</p>' },
          /* from AddFormModal's three entry points */
          { target: '[data-tour="wo-add-form"]', title: 'Forms',
            body: '<p><b>Add Form</b> attaches a digital form from your templates, or uploads a PDF or ' +
                  'photos of a paper form.</p>' }],
        doIt: 'Click <b>Upload</b> on the section you want',
        done: () => uploadOpen() || uploaded() },

      { target: '[data-tour="upload-dropzone"]', place: 'right', avoid: '[data-tour="modal-shell"]',
        title: 'Add your files',
        body: '<p>Drag files onto this box, or click it to choose them from your computer. You can pick ' +
              'several at once.</p>' +
              '<p>The file types this section accepts are listed underneath.</p>',
        doIt: 'Choose or drop a file',
        done: () => uploadStaged() || uploaded() },

      { target: '[data-tour="modal-body"]', also: '[data-tour="modal-shell"] ul',
        place: 'right', avoid: '[data-tour="modal-shell"]', kind: 'info',
        title: 'Check what you’re adding',
        body: () => (uploadKind === 'photos' || uploadKind === 'docs'
          ? '<p>Each file gets its own card. Open it to set a <b>Name</b>, <b>Tags</b> and a <b>Description</b>, ' +
            'and choose whether it prints on the service ticket with <b>Include In Service Ticket</b>.</p>' +
            (uploadKind === 'photos' ? '<p>Videos cannot go on the service ticket.</p>' : '')
          : '<p>Each file is listed with its size. The trash icon takes one off before it is uploaded.</p>') +
          '<p><span class="k">Add More</span>, bottom left, adds more files to the same upload.</p>',
        done: () => uploaded() },

      { target: '[data-tour="upload-all"]', place: 'right', avoid: '[data-tour="modal-shell"]',
        title: 'Upload',
        body: '<p>Nothing is saved until you click it. The number in brackets is how many files will go up.</p>',
        doIt: 'Click <b>Upload All</b>',
        done: () => uploaded() },

      { kind: 'finish', place: 'top',
        target: () => '[data-tour="wo-sec-' + (uploadKind || 'photos') + '"]',
        prep(){ const el = document.querySelector('[data-tour="wo-sec-' + (uploadKind || 'photos') + '"]'); if (el) el.scrollIntoView({ block: 'center' }); },
        title: 'It’s on the work order',
        body: () => '<p>Your file is in <b>' + uploadLabel() + '</b>.</p>' +
          (uploadKind === 'photos'
            ? '<p>Each photo card keeps its <b>Include In Service Ticket</b> toggle, for changing whether it ' +
              'prints later.</p>'
            : uploadKind === 'docs' ? ''
            : '<p>Click its name to open it; Download and Delete are on the right of its row.</p>') }
    ]
  }
};

/* ---------------- badge state (read by header.js) ---------------- */
export const wtBadgeState = () => SESSION.getItem('makaniKbWtBadge') || '';   /* '' | 'done' | 'paused' */
export const wtBadgeSlug = () => SESSION.getItem('makaniKbWtSlug') || '';
const setBadge = (state, slug) => {
  SESSION.setItem('makaniKbWtBadge', state || '');
  if (slug) SESSION.setItem('makaniKbWtSlug', slug);
  announce();
};

/* ---------------- the component ---------------- */
export default function KbWalkthrough(){
  const navigate = useNavigate();
  const location = useLocation();
  const root = useRef(null);
  const eng = useRef(null);

  /* The engine lives in a ref so its geometry work never goes through
     React. It is created once and given the latest navigate each render. */
  if (!eng.current) eng.current = createEngine();
  eng.current.navigate = navigate;

  useEffect(() => {
    const e = eng.current;
    e.mount(root.current);
    const onStart = ev => e.start(ev.detail.slug, 'auto');
    const onResume = ev => e.start(ev.detail.slug, ev.detail.resume);
    const onKey = ev => { if (e.run && ev.key === 'Escape'){ ev.preventDefault(); e.end('paused'); } };
    window.addEventListener(KB_WALK_START, onStart);
    window.addEventListener('makani-kb-walk-resume', onResume);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(KB_WALK_START, onStart);
      window.removeEventListener('makani-kb-walk-resume', onResume);
      document.removeEventListener('keydown', onKey);
      e.unmount();
    };
  }, []);

  /* A route change while running: the target may have moved or vanished. */
  useEffect(() => { if (eng.current.run) eng.current.paint(); }, [location.pathname, location.search]);

  return (
    <div className="kb-wt" ref={root}>
      <div className="wt" data-kbwt="root">
        <div className="wt-dim" />
        <div className="wt-ring hide" />
        <div className="wt-ring wt-ring2 hide" />
        {/* outline-only rings for an open step; the engine owns the children */}
        <div className="wt-outlines" />
        <div className="wt-tip" />
        {/* callout tiles for a step with several controls; engine-owned too.
            After the main tile, so '.wt-tip' always finds the main one first. */}
        <div className="wt-callouts" />
      </div>
    </div>
  );
}

/* ---------------- the engine ---------------- */
function createEngine(){
  const E = {
    run: null, slug: null, step: 0, prepped: -1, navigate: null,
    els: null, watch: 0, repaintQueued: false,
  };

  E.mount = root => {
    E.els = {
      wt: root.querySelector('.wt'), dim: root.querySelector('.wt-dim'),
      ring: root.querySelector('.wt-ring'), ring2: root.querySelector('.wt-ring2'),
      outlines: root.querySelector('.wt-outlines'), callouts: root.querySelector('.wt-callouts'), tip: root.querySelector('.wt-tip')
    };
    E.els.wt.addEventListener('click', E.onClick);
  };
  E.unmount = () => { E.stopWatch(); if (E.els) E.els.wt.removeEventListener('click', E.onClick); };

  const stepDef = () => (E.run ? E.run.steps[E.step] : null);

  /* ---- running ---- */
  E.start = (slug, resume) => {
    const w = WALKTHROUGHS[slug];
    if (!w) return;
    /* a fresh start ("Take me there"), as opposed to Resume from the badge */
    const fresh = resume === 'auto' || !resume;
    if (resume === 'auto') resume = !!(w.midTask && w.midTask());
    if (fresh && w.begin) w.begin();
    E.run = w; E.slug = slug; E.step = 0; E.reach = 0; E.held = null; E.prepped = -1;
    E.placedStep = null; E.settleStep = null; clearTimeout(E.placeTimer); E.placeTimer = 0;
    E.lostSince = 0; clearTimeout(E.lostTimer); E.lostTimer = 0;
    setBadge('', slug);
    setLastArticle(slug); announce();          /* the article rides along in the drawer */
    setWalkthroughRunning(true);
    /* The drawer is dimmed and unreadable while a walkthrough runs, and at a
       laptop width it takes the room the tooltip needs beside the dialog
       (sidebar 257 + dialog 436 + gap + tooltip 330). When that is the case,
       tuck it away for the duration and put it back afterwards; on a wide
       screen it stays where it is. */
    const dw = readJson('makaniKbDrawer', { dismissed: false, hidden: false, width: 400 });
    const roomBeside = window.innerWidth - 257 - (dw.dismissed ? 0 : Math.min(dw.width, 760)) - 436 - 14 - 330 - 24;
    E.drawerWas = null;
    const showing = !dw.dismissed || !!document.querySelector('.kb.drawer.on');
    if (showing && roomBeside < 0){ E.drawerWas = { ...dw, dismissed: false }; writeJson('makaniKbDrawer', { ...dw, dismissed: true }); announce(); }
    if (!resume && w.reset) w.reset(E.navigate);
    /* a walkthrough may have more than one home page — the assign one also
       runs on an open work order, which must not be navigated away from */
    else if (!(w.onHome ? w.onHome() : onWorkList())) E.navigate(w.leftFrom || '/work?branch=0', { replace: true });
    w.leftFrom = null;
    E.els.wt.classList.add('on');
    E.startWatch();
    /* the page may be mid-navigation; paint once the DOM has caught up */
    setTimeout(() => {
      if (!E.run) return;
      if (resume) E.enter(E.landingStep());
      E.paint();
    }, 60);
  };

  E.end = how => {
    setBadge(how, E.slug);
    E.run = null; E.step = 0;
    E.els.wt.classList.remove('on');
    E.stopWatch();
    setWalkthroughRunning(false);
    if (E.drawerWas){ writeJson('makaniKbDrawer', { ...E.drawerWas, dismissed: false }); E.drawerWas = null; announce(); }
  };

  /* Revisiting. A step the reader went Back to is usually already done — the
     branch is still picked — so left to the watcher it would flick straight
     forward again, and Back would do nothing but flash. Any step behind the
     furthest one reached (E.reach) is therefore "held": it waits for a fresh
     action rather than a true done(). Fresh means done() goes false and back
     to true, or the page moves (a different branch changes the URL). Next
     always works. Steps at or past E.reach behave exactly as before, so
     resume and doing things out of order are untouched. */
  const doneAt = i => { const s = E.run.steps[i]; return !!(s && s.done && s.done()); };
  E.enter = i => {
    E.step = i;
    if (i < E.reach) E.held = { href: window.location.href, done: doneAt(i) };
    else { E.reach = i; E.held = null; }
  };

  E.next = () => {
    if (!E.run) return;
    if (E.step >= E.run.steps.length - 1){ E.end('done'); return; }
    E.enter(E.step + 1); E.paint();
  };
  E.back = () => { if (E.run && E.step > 0){ E.enter(E.step - 1); E.paint(); } };

  /* Where a resume should pick the reader up: the first step still to do,
     walking back to the last one whose target is actually on screen. */
  E.landingStep = () => {
    const steps = E.run.steps;
    let i = (steps[0].kind === 'intro' && steps.length > 1) ? 1 : 0;
    while (i < steps.length - 1 && steps[i].done && steps[i].done()) i++;
    const onScreen = n => !sel(steps[n]) || !!document.querySelector(sel(steps[n]));
    while (i > 0 && !onScreen(i)) i--;
    return i;
  };

  /* Advances past every step whose done() already holds. A timer, not
     requestAnimationFrame, because rAF does not tick in every embedded
     browser and a watchdog that silently never runs is worse than none.
     Also repaints when the target's geometry changes, which covers the
     dialog scrolling and the table scrolling sideways. */
  let last = '';
  E.startWatch = () => {
    clearInterval(E.watch);
    E.watch = setInterval(() => {
      if (!E.run){ E.stopWatch(); return; }
      /* pauseWhen: the reader has plainly gone off to do their own thing (the
         find walkthrough: they opened the work order they were looking for).
         Fold into the header badge rather than fight them, and remember the
         list they left so Resume brings them back to it, search and all. */
      const home = E.run.onHome ? E.run.onHome() : onWorkList();
      if (home) E.lastHome = window.location.pathname + window.location.search;
      if (E.run.pauseWhen && E.run.pauseWhen()){ E.run.leftFrom = E.lastHome; E.end('paused'); return; }
      let moved = false;
      while (E.step < E.run.steps.length - 1){
        const cur = E.run.steps[E.step];
        /* only Next moves a manual step — unless the reader has plainly gone
           ahead of it (leaveWhen), e.g. opened a work order without searching */
        if (cur.manual && !(cur.leaveWhen && cur.leaveWhen())) break;
        const d = doneAt(E.step);
        if (E.held){
          if (!d){ E.held.done = false; break; }             /* undone: redoing it counts */
          if (E.held.done && window.location.href === E.held.href) break;
        } else if (!d) break;
        E.enter(E.step + 1); moved = true;
        if (E.held) break;                                   /* landed on another revisited step */
      }
      const b = E.rect(), b2 = b && E.rect2();
      const geo = r => (r ? [r.x, r.y, r.w, r.h].map(Math.round).join(',') : 'none');
      const s = E.run.steps[E.step];
      const yieldNow = !!(s.yieldTo && document.querySelector(s.yieldTo));
      const key = geo(b) + '|' + geo(b2) + '|' + (s.outline ? E.outlineKey(s) : '') + '|' + yieldNow + '|' + E.step + '|' + (E.held ? +doneAt(E.step) : '');
      if (moved || key !== last){ last = key; E.paint(false, true); }
    }, 100);
    document.addEventListener('scroll', E.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', E.onScroll, { passive: true });
  };
  E.stopWatch = () => {
    clearInterval(E.watch); E.watch = 0;
    document.removeEventListener('scroll', E.onScroll, { capture: true });
    window.removeEventListener('resize', E.onScroll);
  };

  /* Scrolling. The 100ms watcher alone let rings and tiles trail the page by
     up to a tenth of a second, and their own position transitions made them
     glide after it. So a real scroll (any scroller — capture phase) repaints
     once per frame, and while scrolling everything snaps (.wt.scrolling).
     rAF with a timer fallback, because rAF does not tick in every embedded
     browser (see the watcher); whichever fires first paints. Where scroll
     events do not fire at all (the preview pane), the watcher still covers. */
  E.onScroll = () => {
    if (!E.run || !E.els) return;
    if (Date.now() > (E.quietScroll || 0)){                /* the reader's scroll, not a prep's */
      E.els.wt.classList.add('scrolling');
      clearTimeout(E.scrollEnd);
      E.scrollEnd = setTimeout(() => E.els && E.els.wt.classList.remove('scrolling'), 150);
    }
    if (E.scrollQueued) return;
    E.scrollQueued = true;
    const run = () => { if (!E.scrollQueued) return; E.scrollQueued = false; if (E.run) E.paint(false, true); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    setTimeout(run, 16);
  };

  /* ---- geometry ---- */
  const union = (a, b) => {
    if (!a) return b; if (!b) return a;
    const x1 = Math.min(a.x, b.x), y1 = Math.min(a.y, b.y);
    const x2 = Math.max(a.x + a.w, b.x + b.w), y2 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  };
  const boxOf = el => { const r = el.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; };
  /* the Assign window's menus render hidden for a frame while they measure */
  const visible = el => getComputedStyle(el).visibility !== 'hidden';

  /* Open popups the step names in also: — every match, not just the first,
     and only ones with a size (a closed list is either absent or 0×0). */
  E.popups = () => {
    const s = stepDef();
    if (!s || !s.also) return [];
    return [...document.querySelectorAll(s.also)].filter(visible)
      .map(el => clipToScrollers(el, boxOf(el)))
      .filter(b => b.w > 0 && b.h > 0);
  };
  /* What the ring outlines while a list is open: the list plus the field that
     opened it (its wrapper), as one control — so the ring never runs across
     the open list. The wrapper is skipped if it is the step's whole target. */
  E.popupRing = () => {
    const s = stepDef();
    if (!s || !s.also) return null;
    const target = sel(s) && document.querySelector(sel(s));
    let box = null;
    [...document.querySelectorAll(s.also)].filter(visible).forEach(el => {
      const b = clipToScrollers(el, boxOf(el));
      if (!(b.w > 0 && b.h > 0)) return;
      const wrap = el.parentElement;
      const withField = wrap && wrap !== target && !wrap.contains(target) ? union(b, clipToScrollers(wrap, boxOf(wrap))) : b;
      box = union(box, withField);
    });
    return box;
  };
  /* withPopups false gives the target alone — what the tooltip is placed
     against, so it does not jump every time a list opens */
  E.rect = (withPopups = true) => {
    const s = stepDef();
    if (!s || !sel(s)) return null;
    const el = document.querySelector(sel(s));
    if (!el) return null;
    let box = boxOf(el);
    if (s.targetEnd){ const e2 = document.querySelector(s.targetEnd); if (e2) box = union(box, boxOf(e2)); }
    box = clipToScrollers(el, box);
    /* an open picker hangs outside its trigger; the hole is the union, each
       popup clipped to its own scrollers (a list inside the scrolling create
       dialog really is cut off by it) */
    if (withPopups) E.popups().forEach(p => { box = union(box, p); });
    return box;
  };
  /* A second, separate hole — for a step about two controls that are not
     next to each other (the two sortable headings), where one union would
     light up everything in between. */
  E.rect2 = () => {
    const s = stepDef();
    /* extra may be a function: a second hole that only opens once something
       has happened (the results, once a search is in place) */
    const q = s && (typeof s.extra === 'function' ? s.extra() : s.extra);
    const el = q ? document.querySelector(q) : null;
    return el ? clipToScrollers(el, boxOf(el)) : null;
  };
  const clipToScrollers = (el, box) => {
    /* clip to whatever scrolls the target — a row wider than its table
       would otherwise light up empty space under the app's own menu.
       A position:fixed element escapes its ancestors' overflow (the Assign
       window's Team and Role menus), so it is not clipped by them. */
    if (getComputedStyle(el).position === 'fixed') return box;
    let p = el.parentElement;
    while (p && p !== document.body){
      const cs = getComputedStyle(p);
      if (/auto|scroll|hidden/.test(cs.overflowX + ' ' + cs.overflowY)){
        const c = p.getBoundingClientRect();
        const x1 = Math.max(box.x, c.left), y1 = Math.max(box.y, c.top);
        const x2 = Math.min(box.x + box.w, c.right), y2 = Math.min(box.y + box.h, c.bottom);
        box = { x: x1, y: y1, w: Math.max(0, x2 - x1), h: Math.max(0, y2 - y1) };
      }
      /* Nothing above a fixed ancestor clips it. The upload windows are
         rendered inside their section's overflow:hidden card but are
         position:fixed, so walking on cut the dropzone down to the card. */
      if (cs.position === 'fixed') break;
      p = p.parentElement;
    }
    return box;
  };

  /* Outline-only rings (outline: [selectors]) — every control the reader may
     use on an open step, drawn without a hole or a pulse. A control scrolled
     out of view gets no ring, and neither does one an open list is lying
     over: the ring sits above the page, so it would cut across the list. */
  /* One ring per selector, always the same ring (index = position in
     outline:), so hiding one never shifts the others onto new controls. */
  const outlineBoxes = s => (s.outline || []).map(q => {
    const el = document.querySelector(q);
    const b = el && clipToScrollers(el, boxOf(el));
    return b && b.w >= 20 && b.h > 0 ? b : null;
  });
  E.paintOutlines = (s, popups) => {
    const host = E.els.outlines, pad = 6;
    const hit = (a, p) => !(a.x + a.w <= p.x || a.x >= p.x + p.w || a.y + a.h <= p.y || a.y >= p.y + p.h);
    /* the rule: anything open lying over a ring hides it until it closes */
    const boxes = outlineBoxes(s)
      .map(b => (b ? { x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 } : null))
      .map(b => (b && !popups.some(p => hit(b, p)) ? b : null));
    while (host.children.length < boxes.length){
      const r = document.createElement('div'); r.className = 'wt-ring wt-outline hide'; host.appendChild(r);
    }
    [...host.children].forEach((r, i) => {
      const b = boxes[i];
      if (!b){ r.classList.add('hide'); return; }
      r.classList.remove('hide');
      r.style.left = b.x + 'px'; r.style.top = b.y + 'px'; r.style.width = b.w + 'px'; r.style.height = b.h + 'px';
    });
  };
  E.outlineKey = s => outlineBoxes(s).map(b => (b ? [b.x, b.y, b.w, b.h].map(Math.round).join(',') : '-')).join(';') + '|' + E.popups().length;

  /* Callouts (callouts: [{ target, title, body, place }]) — small tiles of
     their own, one per control, alongside the step's main tile (which carries
     the count and the buttons). Text only: they take no clicks, so nothing
     under them is blocked. Each sits beside its control on the first side
     that is on screen and clear of the main tile, the other callouts and
     every outlined control; one whose control is scrolled away is hidden. */
  /* Beside box b, on the first side that is on screen and clear of every
     rect in taken; failing that, the preferred side pulled onto the screen. */
  const placeBeside = (b, cw, ch, pref, taken) => {
    const W = window.innerWidth, H = window.innerHeight, gap = 12, edge = 12;
    const m = 8;                                   /* clear of the ring drawn round each control */
    const hit = (a, t) => !(a.x + a.w <= t.x - m || a.x >= t.x + t.w + m || a.y + a.h <= t.y - m || a.y >= t.y + t.h + m);
    const at = p => {
      if (p === 'left')  return { x: b.x - gap - cw, y: b.y + b.h / 2 - ch / 2 };
      if (p === 'right') return { x: b.x + b.w + gap, y: b.y + b.h / 2 - ch / 2 };
      if (p === 'top')   return { x: b.x + b.w - cw, y: b.y - gap - ch };
      return { x: b.x + b.w - cw, y: b.y + b.h + gap };            /* bottom, right-aligned */
    };
    const onScreen = r => r.x >= edge && r.y >= edge && r.x + cw <= W - edge && r.y + ch <= H - edge;
    for (const p of [pref || 'left', 'bottom', 'top', 'right', 'left']){
      const r = at(p);
      if (onScreen(r) && !taken.some(t => hit({ x: r.x, y: r.y, w: cw, h: ch }, t))) return r;
    }
    const r = at(pref || 'left');
    return { x: Math.min(Math.max(edge, r.x), W - cw - edge), y: Math.min(Math.max(edge, r.y), H - ch - edge) };
  };
  E.placeBeside = placeBeside;

  E.paintCallouts = (s, mainTip) => {
    const host = E.els.callouts, list = s.callouts || [];
    /* outlineBoxes keeps a null for a control scrolled out of view (so each
       ring stays on its own control); placement only wants the real ones */
    const taken = [mainTip, ...outlineBoxes(s).filter(Boolean)];
    while (host.children.length < list.length){
      const c = document.createElement('div'); c.className = 'wt-tip wt-callout hide'; host.appendChild(c);
    }
    [...host.children].forEach((c, i) => {
      const def = list[i];
      const el = def && document.querySelector(def.target);
      const b = el && clipToScrollers(el, boxOf(el));
      if (!b || b.w < 20 || b.h <= 0){ c.classList.add('hide'); return; }
      c.classList.remove('hide');
      const html = '<h4>' + def.title + '</h4>' + def.body;
      if (c.innerHTML !== html) c.innerHTML = html;
      const cw = c.offsetWidth, ch = c.offsetHeight;
      const pos = placeBeside(b, cw, ch, def.place, taken);
      c.style.left = pos.x + 'px'; c.style.top = pos.y + 'px';
      taken.push({ x: pos.x, y: pos.y, w: cw, h: ch });
    });
  };

  /* ---- painting ---- */
  E.paint = (scrollIntoView, isFollowUp) => {
    if (!E.run) return;
    const s = stepDef();
    /* prep runs once per step — but not before the step's target exists, or the
       finish step would try to scroll to a row the list has not fetched yet */
    /* the scroll a prep does is the tour's own, not the reader's: it must not
       switch off the tile's glide to its new place (see E.onScroll) */
    if (E.prepped !== E.step && (!sel(s) || document.querySelector(sel(s)))){ E.prepped = E.step; if (s.prep){ E.quietScroll = Date.now() + 250; s.prep(); } }
    const pad = 6, WT_R = 9;
    /* up to two holes; one scrolled wholly out of view is dropped, so on a
       two-target step whichever heading is on screen keeps the ring */
    const shown = b => !!b && b.w >= 20 && b.h > 0;       /* a sliver at the table's edge is not "shown" */
    /* The target alone: the ring goes round it and the tooltip is placed
       against it, so neither jumps when a dropdown opens. */
    const r1 = E.rect(false), r2 = r1 && E.rect2();
    const both = [r1, r2].filter(shown);
    const main = both[0] || r1, second = both[1] || null;
    /* the tooltip is placed against both holes together */
    /* placeOnMain: the second hole is a big region that comes and goes, and
       the tile must not jump across the screen when it does */
    const box = second && !s.placeOnMain ? union(main, second) : main;
    const W = window.innerWidth, H = window.innerHeight;
    const px = n => n + 'px';
    const { wt, dim, ring, ring2, tip } = E.els;

    /* An app dialog with its own backdrop is up (the filter's date picker):
       step aside entirely until it closes. The watcher repaints on the change. */
    E.yielding = !!(s.yieldTo && document.querySelector(s.yieldTo));
    wt.classList.toggle('yield', E.yielding);
    /* A view step: the tour has just brought the reader to a new screen, and
       one tile says what is on it before the next task. No dim — the point is
       to look at the page — and only Next moves it (kind:'info'). */
    wt.classList.toggle('open', !!s.view);

    const popups = (main || s.outline) ? E.popups() : [];
    /* dimOnly wins even when the step has a target: the target is then only
       what the tile is placed against (the callout step), not a hole */
    if (!main || s.dimOnly){
      /* An open step (dimOnly): no hole at all — the dim covers just the one
         element named, the app's own side menu, and the rest of the page is
         left fully usable. */
      const only = s.dimOnly && document.querySelector(s.dimOnly);
      if (only){
        const b = boxOf(only), d = dim.getBoundingClientRect();
        dim.style.clipPath = 'path("M' + (b.x - d.left) + ' ' + (b.y - d.top) + ' h' + b.w + ' v' + b.h + ' h' + (-b.w) + ' Z")';
      } else dim.style.clipPath = s.dimOnly ? 'polygon(0 0, 0 0, 0 0)' : 'none';
      ring.classList.add('hide');
    } else {
      const d = dim.getBoundingClientRect();
      const n = v => Math.round(v * 100) / 100;
      const outer = 'M0 0 H' + n(d.width) + ' V' + n(d.height) + ' H0 Z';
      const padded = b => ({ x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 });
      /* a rounded rect; rt / rb let a popup's hole meet its target's square */
      const rrect = (b, rt, rb) => {
        const X = b.x - d.left, Y = b.y - d.top, w = b.w, h = b.h;
        const a = Math.max(0, Math.min(rt, w / 2, h / 2)), c = Math.max(0, Math.min(rb, w / 2, h / 2));
        return 'M' + n(X + a) + ' ' + n(Y) +
          ' H' + n(X + w - a) + ' A' + n(a) + ' ' + n(a) + ' 0 0 1 ' + n(X + w) + ' ' + n(Y + a) +
          ' V' + n(Y + h - c) + ' A' + n(c) + ' ' + n(c) + ' 0 0 1 ' + n(X + w - c) + ' ' + n(Y + h) +
          ' H' + n(X + c) + ' A' + n(c) + ' ' + n(c) + ' 0 0 1 ' + n(X) + ' ' + n(Y + h - c) +
          ' V' + n(Y + a) + ' A' + n(a) + ' ' + n(a) + ' 0 0 1 ' + n(X + a) + ' ' + n(Y) + ' Z';
      };
      const holeFor = (b, rg) => {
        const p = padded(b);
        rg.classList.remove('hide');
        rg.style.left = px(p.x); rg.style.top = px(p.y); rg.style.width = px(p.w); rg.style.height = px(p.h);
        return rrect(p, WT_R, WT_R);
      };
      let path = outer + ' ' + holeFor(main, ring);
      if (second) path += ' ' + holeFor(second, ring2);
      /* Each open list gets its own hole, butted against the target's rather
         than merged into one big rectangle — a union would light up every row
         beside the list too. Under evenodd two overlapping holes cancel back
         to dim, so the part of the list already inside the target's hole is
         trimmed off, and the join is square. */
      const M = padded(main);
      popups.forEach(pb => {
        const P = padded(pb);
        const overlapX = P.x < M.x + M.w && P.x + P.w > M.x;
        const overlapY = P.y < M.y + M.h && P.y + P.h > M.y;
        if (!(overlapX && overlapY)){ path += ' ' + rrect(P, WT_R, WT_R); return; }
        if (P.y + P.h > M.y + M.h){                         /* hangs below: the usual case */
          const top = M.y + M.h;
          path += ' ' + rrect({ x: P.x, y: top, w: P.w, h: P.y + P.h - top }, 0, WT_R);
        } else if (P.y < M.y){                              /* opens upward */
          path += ' ' + rrect({ x: P.x, y: P.y, w: P.w, h: M.y - P.y }, WT_R, 0);
        }                                                   /* else wholly inside: already lit */
      });
      dim.style.clipPath = 'path(evenodd, "' + path + '")';
      /* with a list open, the ring leaves the target and outlines the list
         and its field instead — it slides there on its own transition */
      const pr = popups.length ? E.popupRing() : null;
      if (pr){
        const p = padded(pr);
        ring.style.left = px(p.x); ring.style.top = px(p.y); ring.style.width = px(p.w); ring.style.height = px(p.h);
      }
    }
    if (!second) ring2.classList.add('hide');
    E.paintOutlines(s, popups);
    /* pulse only where a click is being asked for */
    const live = !!(main && s.doIt && !(E.held && doneAt(E.step)));
    ring.classList.toggle('live', live);
    /* a region lit alongside the control (placeOnMain) is outlined, not pulsed */
    ring2.classList.toggle('live', live && !!second && !s.placeOnMain);

    /* ---- the tooltip ---- */
    const total = E.run.steps.length, finish = s.kind === 'finish';
    let h = finish
      ? '<div class="wt-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 12.5l5.5 5.5L20 7"/></svg></div>'
      : '<div class="wt-count">Step ' + (E.step + 1) + ' of ' + total + ' · ' + E.run.label + '</div>';
    h += '<h4>' + s.title + '</h4>' + (typeof s.body === 'function' ? s.body() : s.body);
    /* a revisited step that is still done: nothing to do, so say so and offer Next */
    const already = !!E.held && doneAt(E.step);
    if (s.doIt && s.manual) h += '<div class="wt-do"><span class="dot"></span><span>' + s.doIt + ', then click <b>Next</b> to continue</span></div>';
    else if (s.doIt && already) h += '<div class="wt-do"><span class="dot"></span><span>Already done — change it, or press <b>Next</b> to carry on</span></div>';
    else if (s.doIt) h += '<div class="wt-do"><span class="dot"></span><span>' + s.doIt + ' to carry on</span></div>';
    h += '<div class="wt-foot">';
    if (finish){
      h += '<button class="wt-btn quiet" data-wt="article" type="button">Back to the article</button>' +
           '<span class="sp"></span><button class="wt-btn go" data-wt="close" type="button">Done</button>';
    } else {
      h += '<div class="wt-dots">' + E.run.steps.map((_, i) =>
        '<i class="' + (i === E.step ? 'on' : (i < E.step ? 'past' : '')) + '"></i>').join('') + '</div><span class="sp"></span>';
      if (E.step > 0) h += '<button class="wt-btn quiet" data-wt="back" type="button">Back</button>';
      if (s.kind === 'intro') h += '<button class="wt-btn go" data-wt="next" type="button">Start</button>';
      else if (s.kind === 'info' || !s.doIt || already || s.manual) h +='<button class="wt-btn go" data-wt="next" type="button">Next</button>';
      /* noSkip: the next step only makes sense once this one is really done */
      else if (!s.noSkip) h += '<button class="wt-btn line" data-wt="next" type="button">Skip</button>';
      h += '<button class="wt-btn quiet" data-wt="close" type="button" title="Leave the walkthrough">Exit</button>';
    }
    h += '</div>';
    tip.innerHTML = h;
    /* Lost target: the tile was placed on this step and its control has
       since vanished — SUBMIT closing the create dialog, a step before the
       tour moves on. Re-centring then heading for the next target was the
       finish's zigzag, so the tile stays where it is; only a target gone for
       over 1.5s (the reader closed the dialog and wandered off) re-centres. */
    if (sel(s) && !box && E.placedStep === E.step){
      if (!E.lostSince){
        E.lostSince = Date.now();
        /* nothing else repaints a steady "no target", so come back to re-centre */
        clearTimeout(E.lostTimer);
        E.lostTimer = setTimeout(() => { E.lostTimer = 0; if (E.run) E.paint(false, true); }, 1550);
      }
    } else { E.lostSince = 0; clearTimeout(E.lostTimer); E.lostTimer = 0; }
    const lost = !!E.lostSince && Date.now() - E.lostSince < 1500;
    if (!lost) tip.classList.toggle('center', !box && !s.dock);
    tip.classList.toggle('finish', !!finish);

    /* ---- place it: preference, then whatever fits, measured from the
       obstacle (the dialog) rather than the target, so an inset target
       cannot be "cleared" by four pixels ---- */
    const tw = tip.offsetWidth, th = tip.offsetHeight, gap = 14, edge = 12;
    let tx, ty;
    if (s.dock){
      /* docked bottom-right, out of the way of an open step — left of the
         follow-along drawer when it is showing */
      const drawer = document.querySelector('.kb.drawer.on');
      const right = drawer ? drawer.getBoundingClientRect().left : W;
      tx = right - tw - 16; ty = H - th - 16;
    }
    else if (!box){ tx = (W - tw) / 2; ty = Math.max(edge, (H - th) / 2); }
    else {
      const keep = s.avoid ? document.querySelector(s.avoid) : null;
      const kr = keep ? keep.getBoundingClientRect() : null;
      const R = kr ? Math.max(box.x + box.w, kr.right) : box.x + box.w;
      const L = kr ? Math.min(box.x, kr.left) : box.x;
      const B = kr ? Math.max(box.y + box.h, kr.bottom) : box.y + box.h;
      const T = kr ? Math.min(box.y, kr.top) : box.y;
      const fits = { bottom: B + gap + th < H - edge, top: T - gap - th > edge, left: L - gap - tw > edge, right: R + gap + tw < W - edge };
      const at = p => {
        let x, y;
        if (p === 'bottom'){ x = box.x + box.w / 2 - tw / 2; y = B + gap; }
        else if (p === 'top'){ x = box.x + box.w / 2 - tw / 2; y = T - gap - th; }
        else if (p === 'left'){ x = L - gap - tw; y = box.y + box.h / 2 - th / 2; }
        else { x = R + gap; y = box.y + box.h / 2 - th / 2; }
        return { x: Math.min(Math.max(edge, x), W - tw - edge), y: Math.min(Math.max(edge, y), H - th - edge) };
      };
      const order = [s.place || 'bottom', 'bottom', 'top', 'right', 'left'];
      const use = order.find(p => fits[p]) || 'bottom';
      let a = at(use);
      /* Whatever won, it must not sit on the control the step is asking for.
         At a narrow window every placement can overlap the dialog, and the
         fallback then lands on the target itself — on the Submit step that
         covered the SUBMIT button. Shift to whichever side of the target has
         more room; covering fields already filled in is an acceptable cost. */
      const tb = { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 };
      const overlapsTarget = () => !(a.x + tw <= tb.x || a.x >= tb.x + tb.w || a.y + th <= tb.y || a.y >= tb.y + tb.h);
      if (overlapsTarget()){
        const above = tb.y, below = H - (tb.y + tb.h);
        const y = above >= below ? tb.y - gap - th : tb.y + tb.h + gap;
        a = { x: a.x, y: Math.min(Math.max(edge, y), H - th - edge) };
      }
      tx = a.x; ty = a.y;
      /* A step with callouts: the main tile is one of a family of tiles, so
         it is placed the way they are — clear of every outlined control, not
         only its own, or it lands on the next section's Upload button. */
      if (s.callouts){ const p = E.placeBeside(box, tw, th, s.place, outlineBoxes(s).filter(Boolean)); tx = p.x; ty = p.y; }
    }
    /* On a step change the tile holds still for a moment and then moves once.
       Placed straight away, it was sometimes aimed while the app was still
       re-laying the page, and the corrective pass re-aimed it mid-flight —
       a kink in the path. 40ms lets the layout settle; nobody sees the wait.
       The first placement of a run is immediate (there is no path to kink). */
    /* It also waits for the target to exist and hold still. The create
       walkthrough's finish points at the new row, which only appears when the
       list has reloaded, and then moves when the table scrolls to its Status
       column: aimed at each in turn, the tile zigzagged. So: hold until two
       paints 60ms apart agree on the target's box. A target still moving is
       given 1.5s; one that does not exist yet is waiting on the server (the
       list refetch took over 1.5s on devgav), so it is given 8s. */
    let settling = false;
    if (E.placedStep !== null && E.placedStep !== E.step){
      if (E.settleStep !== E.step){ E.settleStep = E.step; E.settleSince = Date.now(); E.settleKey = null; }
      const missing = !!sel(s) && !document.querySelector(sel(s));
      const key = box ? [box.x, box.y, box.w, box.h].map(Math.round).join(',') : 'none';
      const timedOut = Date.now() - E.settleSince > (missing ? 8000 : 1500);
      if (!timedOut && (missing || key !== E.settleKey)){
        settling = true;
        E.settleKey = key;
        clearTimeout(E.placeTimer);
        E.placeTimer = setTimeout(() => { E.placeTimer = 0; if (E.run) E.paint(false, true); }, 60);
      }
    }
    if (!settling && !lost){
      E.placedStep = E.step;
      tip.style.left = tx + 'px'; tip.style.top = ty + 'px';
      /* A docked tile is pinned by its bottom edge: coming from a centred tile
         its height measured now can be short, and a top-anchored tile would
         grow off the bottom of the screen. */
      if (s.dock){ tip.style.top = 'auto'; tip.style.bottom = '16px'; }
      else tip.style.bottom = '';
    }
    /* the tile sits where the target alone put it; if an open list lands
       under it, fade the tile out rather than cover options or hop sides */
    const onTip = popups.some(p => !(tx + tw <= p.x || tx >= p.x + p.w || ty + th <= p.y || ty >= p.y + p.h));
    tip.classList.toggle('duck', onTip);
    E.paintCallouts(s, { x: tx, y: s.dock ? H - th - 16 : ty, w: tw, h: th });

    if (scrollIntoView && box){
      const el = document.querySelector(sel(s));
      if (el && (box.y < 0 || box.y + box.h > H)) el.scrollIntoView({ block: 'center' });
    }
    /* one corrective pass after the current task — layout has not always
       settled when this runs from inside a re-render */
    if (!isFollowUp && !E.repaintQueued){
      E.repaintQueued = true;
      setTimeout(() => { E.repaintQueued = false; if (E.run) E.paint(false, true); });
    }
  };

  /* ---- clicks on the overlay ---- */
  E.onClick = e => {
    const act = e.target.closest && e.target.closest('[data-wt]');
    if (act){
      e.preventDefault(); e.stopPropagation();
      const a = act.dataset.wt;
      if (a === 'next') E.next();
      else if (a === 'back') E.back();
      else if (a === 'close') E.end(stepDef() && stepDef().kind === 'finish' ? 'done' : 'paused');
      else if (a === 'article'){ const slug = E.slug; E.end('done'); E.navigate(KB_BASE + '/a/' + encodeURIComponent(slug)); }
      return;
    }
    /* a click on the dim is a click on the wrong thing — say so */
    if (E.run && e.target.classList.contains('wt-dim')){
      [E.els.ring, E.els.ring2].forEach(ring => {
        ring.classList.remove('nudge'); void ring.offsetWidth; ring.classList.add('nudge');
        ring.addEventListener('animationend', () => ring.classList.remove('nudge'), { once: true });
      });
    }
  };

  return E;
}
